<template>
  <hr
    v-if="separator && articles.length"
    class="h-[1px] border-0 bg-[black]/15 [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]"
  />

  <section v-if="articles.length" class="grid gap-16">
    <h3 class="raw-cta-2">{{ i18n(translations.relatedStories) }}</h3>
    <DraggableRail>
      <li
        v-for="article in articles"
        :key="article.id"
        class="rounded-md relative grid w-[75%] shrink-0 place-content-between gap-12 bg-[white]/25 p-16"
      >
        <!-- Whole card is the hit area: one transparent button spans it and
                     carries the click + accessible name; the visible chip below is
                     decorative. -->
        <button
          type="button"
          class="rounded-md absolute inset-0 z-[1]"
          :aria-label="i18n(article.title)"
          @click="openArticle(article)"
        />
        <div class="grid gap-6">
          <h4 class="raw-heading-3 line-clamp-2 text-balance">
            {{ i18n(article.title) }}
          </h4>
          <p
            v-if="article.excerpt"
            class="raw-body-2 line-clamp-3 !font-[400] text-[--raw-verydarkgrey]"
          >
            {{ i18n(article.excerpt) }}
          </p>
        </div>
        <div class="relative">
          <RevIllustration
            :src="article.image"
            :width="800"
            :height="1000"
            alt=""
            aria-hidden="true"
            loading="lazy"
            class="rounded-md aspect-video w-full object-cover"
          />
          <span
            aria-hidden="true"
            class="raw-glass raw-cta-3 rounded-full absolute bottom-12 right-12 px-16 py-8"
          >
            {{ i18n(translations.readMore) }}
          </span>
        </div>
      </li>
    </DraggableRail>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevIllustration } from '@ds/components/Illustration'

import DraggableRail from '~/scopes/campaign/components/UI/DraggableRail.vue'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import {
  type Article,
  articlesForMetal,
} from '~/scopes/campaign/data/articles/data'

import translations from './RelatedArticles.translations'

const props = defineProps<{
  metalSlug: string
  /** Own separator — the article wants one, the metal panel doesn't. */
  separator?: boolean
  /** Tracking surface of the host view — the rail lives in more than one. */
  zone: string
}>()

const i18n = useI18n()
const { trackClick } = useTracking()
const { activeArticle, navigateToArticle } = useModalState()

// Never offer the article you are already reading — it stays in the route under the
// `?metal=` quick view opened from its own chip.
const articles = computed(() =>
  articlesForMetal(props.metalSlug).filter(
    (article) => article.slug !== activeArticle.value?.slug,
  ),
)

function openArticle(article: Article) {
  trackClick({ zone: props.zone, name: `related-article-${article.id}` })
  navigateToArticle(article)
}
</script>
